import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { BillingCurrencySchema } from '../currency/currency';
import { BillingCapabilitySchema } from '../entitlement-capability/entitlement-capability';
import { BILLING_LIMITS, type BillingLimit } from '../entitlement-limit/entitlement-limit';
import { MoneyAmountSchema } from '../money/money';
import { ProductKeySchema } from '../product-key/product-key';
import { ProductDurationDaysSchema } from '../subscription-product/subscription-product';

/**
 * What this member would pay for this plan through the **direct** channel.
 *
 * The field names are `DirectPaymentQuoteSchema`'s, deliberately: the staff
 * order desk and the member's paywall resolve the same policy through the same
 * `resolveDiscountOutcome`, so the two surfaces must not have different words
 * for the same number.
 *
 * **Nothing is claimed by asking.** A grant may expire, be revoked, or be spent
 * by another order between this answer and the purchase that acts on it — the
 * order's submit re-resolves and claims (P23a3). This is a quote.
 *
 * `grantIds` is here so the paywall can say *which* earned discount is being
 * applied, not so a client can send them back: no purchase route accepts them.
 */
export const MemberProductDirectTermsSchema = z
  .object({
    /** The undiscounted price, resolved in this member's gender scope (U15). */
    listAmount: MoneyAmountSchema,
    /** `0` when nothing applies, which is the ordinary case. */
    discountAmount: MoneyAmountSchema,
    chargedAmount: MoneyAmountSchema,
    grantIds: z.array(UuidSchema),
  })
  .strict();

export type MemberProductDirectTerms = z.infer<typeof MemberProductDirectTermsSchema>;

/**
 * Every declared lever, resolved to the number this plan's tier actually gets.
 *
 * **Complete rather than partial**, and the difference is what keeps the client
 * dumb: every member of `BILLING_LIMITS` must name a registry setting with a
 * numeric coded default, so "this lever has no value" cannot happen. A partial
 * record would make the paywall draw an absent row for a case that does not
 * exist, and would push the duration/feature branch onto the client — which is
 * the one thing `planMode` on the wire exists to prevent.
 *
 * Derived from the catalogue, so a lever added there cannot be forgotten here.
 * A **retired** lever is stripped on parse rather than carried, exactly as
 * `billing.tierLimits`' own declared object does (P26a).
 */
export const MemberProductLimitsSchema = z.object(
  Object.fromEntries(BILLING_LIMITS.map((lever) => [lever, z.number().int().min(0)])) as Record<
    BillingLimit,
    z.ZodNumber
  >,
);

export type MemberProductLimits = z.infer<typeof MemberProductLimitsSchema>;

/**
 * A plan as the member sees it on the paywall.
 *
 * ## What is deliberately gone
 *
 * `id`, `audience`, `sortOrder`, `isActive` and the timestamps. The member is
 * choosing a plan, not inspecting a catalogue row: `audience` in particular is
 * an operator's segmentation fact, and shipping it would tell a member the
 * platform prices them by gender in a payload rather than in the price they are
 * shown. `key` stays because it is what a purchase, an order and a discount's
 * scope all refer to — the client has to send it back.
 *
 * ## `direct` is the whole direct channel, already resolved for this caller
 *
 * Resolved server-side through `getSetting('billing.directPrices', { gender })`
 * (U15) and the member's active grants — the client never sees the scope map,
 * never learns another gender's price, and never computes a discount of its own.
 * `null` means this deployment does not sell this plan directly, which is the
 * ordinary case where IAP is available; the paywall then shows the store's own
 * price, which is the store's to state.
 *
 * **It is one object rather than four fields**, because the figures are one fact
 * or none: a plan with no direct price has nothing to discount, so four
 * separately-nullable numbers would carry an invariant (all null together) that
 * nothing enforces. It also retires the word *price* from this contract — it
 * used to mean the LIST price, and a reader meeting it beside a discount would
 * reasonably assume the opposite (P23c2a).
 *
 * **A store-only plan gets no discount figures, and that is correct**, not a
 * gap: an IAP discount is a store-managed **offer code** (`iap_offer_code_mappings`,
 * P23b3c), never a reduction the platform may apply to the store's own price.
 *
 * `tier` is carried because the paywall renders a tier comparison in `feature`
 * plan mode. In `duration` mode it is inert and the card ignores it.
 *
 * ## `capabilities` and `limits` are what the tier MEANS, resolved server-side
 *
 * `tier` is an operator-written string; on its own the client can only print it.
 * These two fields are what it buys, and they come from `EntitlementService` —
 * the single authority that may read `billing.tierCapabilities` (N14). A client
 * mapping tier → features would be the second evaluator that rule exists to
 * forbid, and it would need the whole commercial policy to do it.
 *
 * In `duration` mode every plan answers the full catalogue and the base limits,
 * so the card renders them the same way in both modes and branches on nothing.
 */
export const MemberProductSchema = z
  .object({
    key: ProductKeySchema,
    name: z.string(),
    description: z.string(),
    tier: z.string(),
    durationDays: ProductDurationDaysSchema,
    /** `null` where the store is the only channel — see the docblock. */
    direct: MemberProductDirectTermsSchema.nullable(),
    /**
     * The currency `direct` is quoted in. Repeated on every product rather
     * than left to `GET /api/app-settings` because a price and its unit must
     * arrive together: a card that has the number but not yet the currency can
     * only draw a bare figure, and P22c2's discounts screen already learned to
     * wait for both rather than half-draw one.
     */
    currency: BillingCurrencySchema,
    iosProductId: z.string().nullable(),
    androidProductId: z.string().nullable(),
    /**
     * What this plan's tier unlocks. Empty is a real answer — a tier that is
     * sold but left out of the capability map — and the paywall draws that card
     * with every row excluded rather than hiding the plan.
     */
    capabilities: z.array(BillingCapabilitySchema),
    limits: MemberProductLimitsSchema,
  })
  .strict();

export type MemberProduct = z.infer<typeof MemberProductSchema>;
